import { applyRef, createDom, eventName, patchProps, styleText, SVG_NS, validAttributeName, VOID } from "./dom";
import { freezeState } from "./freeze";
import { adopt, normalizeRender } from "./h";
import { FRAGMENT, SPEC, TEXT, type ComponentSpec, type Instance, type Observable, type VNode } from "./types";

const queue = new Set<Instance>();
let scheduled = false;
let flushing = false;
let sync = false;

function callHook(inst: Instance, modern: string, legacy: string) {
  const fn = inst[modern] || inst[legacy];
  if (typeof fn === "function") fn.call(inst);
}

function runUser(fn: () => void, errors: unknown[]) {
  try {
    fn();
  } catch (error) {
    errors.push(error);
  }
}

function raise(errors: unknown[]) {
  if (!errors.length) return;
  for (let i = 1; i < errors.length; i++) console.error(errors[i]);
  throw errors[0];
}

const mountQueue: Instance[] = [];
const commits: Array<{ errors: unknown[] }> = [];

function beginCommit() {
  commits.push({ errors: [] });
}

function runMounts(errors: unknown[]) {
  const mounts = mountQueue.splice(0);
  for (let i = 0; i < mounts.length; i++) {
    const inst = mounts[i];
    if (inst._destroyed || !inst._mounted) continue;
    runUser(() => callHook(inst, "onMount", "_didMount"), errors);
  }
}

function endCommit() {
  const { errors } = commits.pop()!;
  if (commits.length) {
    commits[commits.length - 1].errors.push(...errors);
    return;
  }
  runMounts(errors);
  raise(errors);
}

function queueMount(inst: Instance) {
  if (commits.length) mountQueue.push(inst);
  else callHook(inst, "onMount", "_didMount");
}

export function isSpec(value: unknown): value is ComponentSpec {
  return Boolean(value && typeof value === "object" && (value as any)[SPEC]);
}

export function makeInstance(spec: ComponentSpec, props: Record<string, any>): Instance {
  const inst = {
    type: spec,
    props: props ?? {},
    state: undefined as any,
    _vnode: null,
    _parent: null,
    _mounted: false,
    _destroyed: false,
    _rendering: false,
    _dirty: false,
    _placeholder: null,
    _svg: false,
    _unsubs: null,
  } as Instance;

  for (const key of Object.keys(spec)) {
    if (key === "initialState" || Object.hasOwn(inst, key)) continue;
    const val = (spec as Record<string, unknown>)[key];
    (inst as any)[key] = typeof val === "function" ? val.bind(inst) : val;
  }

  inst.setState = (next) => setState(inst, next);
  inst.observe = (store) => observe(inst, store);
  const initial = typeof spec.initialState === "function" ? spec.initialState(props) : spec.initialState;
  const state = initial !== undefined ? initial : {};
  inst.state = import.meta.env.DEV ? freezeState(state) : state;
  return inst;
}

function observe(inst: Instance, store: Observable) {
  const off = store.subscribe(() => schedule(inst));
  (inst._unsubs ??= []).push(off);
  return off;
}

function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>) {
  if (a === b) return true;
  let n = 0;
  for (const k in a) {
    if (!Object.is(a[k], b[k])) return false;
    n++;
  }
  for (const k in b) n--;
  return n === 0;
}

export function setState(inst: Instance, next: unknown) {
  if (inst._destroyed) return;
  const raw = typeof next === "function" ? (next as (s: unknown) => unknown)(inst.state) : next;
  if (Object.is(raw, inst.state)) return;
  inst.state = import.meta.env.DEV ? freezeState(raw) : raw;
  if (import.meta.env.DEV && inst._rendering) console.warn("SvenJS: setState during render; update will flush after");
  schedule(inst);
}

export function schedule(inst: Instance) {
  if (inst._destroyed) return;
  inst._dirty = true;
  queue.add(inst);
  if (sync || flushing) return;
  if (!scheduled) {
    scheduled = true;
    queueMicrotask(flush);
  }
}

export function flush() {
  scheduled = false;
  const wasFlushing = flushing;
  flushing = true;
  const errors: unknown[] = [];
  try {
    for (let pass = 0; queue.size; pass++) {
      if (pass === 50) {
        if (import.meta.env.DEV) console.warn("SvenJS: update flush exceeded 50 passes; deferring the rest");
        break;
      }
      const list = [...queue];
      queue.clear();
      beginCommit();
      try {
        for (const inst of list) {
          if (inst._destroyed || !inst._mounted) continue;
          runUser(() => updateInstance(inst), errors);
        }
      } finally {
        // Finish this pass before draining cascading updates. A nested flush
        // still belongs to its outer DOM commit and must defer its mount hooks.
        runUser(endCommit, errors);
      }
    }
  } finally {
    flushing = wasFlushing;
    if (queue.size && !scheduled) {
      scheduled = true;
      queueMicrotask(flush);
    }
  }
  raise(errors);
}

export function flushSync(fn?: () => void) {
  const was = sync;
  sync = true;
  let failed = false;
  let failure: unknown;
  try {
    try {
      fn?.();
    } catch (error) {
      failed = true;
      failure = error;
    }
    try {
      flush();
    } catch (error) {
      if (!failed) failure = error;
      failed = true;
    }
  } finally {
    sync = was;
  }
  if (failed) throw failure;
}

function liveStart(vnode: VNode | null | undefined): Node | null {
  if (!vnode) return null;
  if (isSpec(vnode.type) && vnode._instance) {
    const inst = vnode._instance;
    return inst._vnode ? liveStart(inst._vnode) : inst._placeholder;
  }
  return vnode._dom ?? null;
}

function liveEnd(vnode: VNode | null | undefined): Node | null {
  if (!vnode) return null;
  if (isSpec(vnode.type) && vnode._instance) {
    const inst = vnode._instance;
    return inst._vnode ? liveEnd(inst._vnode) : inst._placeholder;
  }
  return vnode._end ?? vnode._dom ?? null;
}

function moveVNode(parent: Node, vnode: VNode, next: Node | null) {
  const start = liveStart(vnode);
  if (!start) return;
  const end = liveEnd(vnode) ?? start;
  if (end.nextSibling === next) return;
  let node: Node | null = start;
  while (node) {
    const following: Node | null = node.nextSibling;
    parent.insertBefore(node, next);
    if (node === end) break;
    node = following;
  }
}

function renderInstance(inst: Instance): VNode | null {
  inst._rendering = true;
  try {
    return adopt(normalizeRender(inst.render()));
  } finally {
    inst._rendering = false;
  }
}

function warnKeys(children: VNode[]) {
  if (children.length < 2) return;
  const keys = children.map((c) => c.key);
  const some = keys.some((k) => k != null);
  const all = keys.every((k) => k != null);
  if (some && !all) {
    console.warn("SvenJS: some list children have keys, some do not");
  }
  if (all) {
    const seen = new Set<string | number>();
    for (const k of keys) {
      if (k == null) continue;
      if (seen.has(k)) console.warn(`SvenJS: duplicate key "${String(k)}"`);
      seen.add(k);
    }
  }
}

function mount(vnode: VNode, parent: Node, anchor: Node | null, svg = false) {
  if (vnode.type === TEXT) {
    const el = document.createTextNode(String(vnode.props.nodeValue ?? ""));
    vnode._dom = el;
    parent.insertBefore(el, anchor);
    return;
  }

  if (vnode.type === FRAGMENT) {
    const start = document.createComment("");
    const end = document.createComment("");
    vnode._dom = start;
    vnode._end = end;
    parent.insertBefore(start, anchor);
    parent.insertBefore(end, anchor);
    if (import.meta.env.DEV) warnKeys(vnode.children);
    for (const child of vnode.children) mount(child, parent, end, svg);
    return;
  }

  if (isSpec(vnode.type)) {
    mountComponent(vnode, parent, anchor, svg);
    return;
  }

  const tag = vnode.type as string;
  const elementSvg = svg || tag === "svg";
  const childSvg = elementSvg && tag !== "foreignObject";
  const el = createDom(tag, elementSvg);
  vnode._dom = el;
  patchProps(el, {}, vnode.props);
  if (import.meta.env.DEV) warnKeys(vnode.children);
  if (vnode.props.dangerouslySetInnerHTML) {
    if (import.meta.env.DEV && vnode.children.length) {
      console.warn("SvenJS: dangerouslySetInnerHTML ignored children");
    }
  } else {
    for (const child of vnode.children) mount(child, el, null, childSvg);
  }
  applyFormProps(el, tag, vnode.props);
  parent.insertBefore(el, anchor);
  applyRef(el, vnode.props.ref);
}

function mountComponent(vnode: VNode, parent: Node, anchor: Node | null, svg = false) {
  const spec = vnode.type as ComponentSpec;
  const inst = makeInstance(spec, vnode.props);
  vnode._instance = inst;
  inst._parent = parent;
  inst._svg = svg;
  callHook(inst, "onBeforeMount", "_beforeMount");
  const rendered = renderInstance(inst);
  inst._vnode = rendered;
  if (rendered) {
    mount(rendered, parent, anchor, svg);
  } else {
    inst._placeholder = document.createComment("");
    parent.insertBefore(inst._placeholder, anchor);
  }
  inst._mounted = true;
  vnode._dom = rendered?._dom ?? inst._placeholder;
  vnode._end = rendered?._end ?? null;
  queueMount(inst);
}

function unmount(vnode: VNode | null | undefined, removeDom = true, errors?: unknown[]) {
  if (!vnode) return;
  const pending = errors ?? commits[commits.length - 1]?.errors;
  const owned = !pending;
  const bag = pending ?? [];
  if (isSpec(vnode.type)) {
    const inst = vnode._instance;
    if (inst && !inst._destroyed) {
      inst._destroyed = true;
      inst._mounted = false;
      if (inst._unsubs) {
        for (const off of inst._unsubs) runUser(off, bag);
        inst._unsubs = null;
      }
      runUser(() => callHook(inst, "onDestroy", "_willUnmount"), bag);
      unmount(inst._vnode, removeDom, bag);
      if (removeDom) inst._placeholder?.parentNode?.removeChild(inst._placeholder);
      inst._vnode = null;
      inst._placeholder = null;
      inst._parent = null;
    }
  } else if (vnode.type === TEXT) {
    if (removeDom) vnode._dom?.parentNode?.removeChild(vnode._dom);
  } else if (vnode.type === FRAGMENT) {
    for (const c of vnode.children) unmount(c, removeDom, bag);
    if (removeDom) {
      vnode._dom?.parentNode?.removeChild(vnode._dom);
      vnode._end?.parentNode?.removeChild(vnode._end);
    }
  } else {
    if (vnode._dom) runUser(() => applyRef(vnode._dom as Element), bag);
    if (!vnode.props.dangerouslySetInnerHTML) {
      for (const c of vnode.children) unmount(c, false, bag);
    }
    if (removeDom) vnode._dom?.parentNode?.removeChild(vnode._dom);
  }
  if (owned) raise(bag);
}

function patch(parent: Node, oldV: VNode | null | undefined, newV: VNode | null | undefined, svg = false) {
  if (oldV === newV) return;
  if (!newV) {
    unmount(oldV);
    return;
  }
  if (!oldV) {
    mount(newV, parent, null, svg);
    return;
  }
  if (oldV.type !== newV.type || oldV.key !== newV.key) {
    const anchor = liveEnd(oldV)?.nextSibling ?? null;
    unmount(oldV);
    mount(newV, parent, anchor, svg);
    return;
  }

  if (newV.type === TEXT) {
    newV._dom = oldV._dom;
    if (oldV.props.nodeValue !== newV.props.nodeValue) {
      (newV._dom as Text).nodeValue = String(newV.props.nodeValue ?? "");
    }
    return;
  }

  if (newV.type === FRAGMENT) {
    newV._dom = oldV._dom;
    newV._end = oldV._end;
    patchChildren(parent, oldV.children, newV.children, svg, newV._end ?? null);
    return;
  }

  if (isSpec(newV.type)) {
    patchComponent(oldV, newV, svg);
    return;
  }

  const el = oldV._dom as Element;
  newV._dom = el;
  const elementSvg = svg || newV.type === "svg";
  const childSvg = elementSvg && newV.type !== "foreignObject";
  const oldHtml = oldV.props.dangerouslySetInnerHTML;
  const newHtml = newV.props.dangerouslySetInnerHTML;
  patchProps(el, oldV.props, newV.props);
  if (newHtml) {
    if (!oldHtml) {
      for (const c of oldV.children) unmount(c, false);
    }
    if (import.meta.env.DEV && newV.children.length) {
      console.warn("SvenJS: dangerouslySetInnerHTML ignored children");
    }
  } else if (oldHtml) {
    el.innerHTML = "";
    for (const child of newV.children) mount(child, el, null, childSvg);
  } else {
    patchChildren(el, oldV.children, newV.children, childSvg);
  }
  applyFormProps(el, newV.type as string, newV.props);
  if (oldV.props.ref !== newV.props.ref) {
    applyRef(el, newV.props.ref);
  }
}

function patchRendered(inst: Instance, rendered: VNode | null, svg: boolean) {
  const old = inst._vnode;
  const parent = inst._parent!;
  const anchor = (old ? liveEnd(old) : inst._placeholder)?.nextSibling ?? null;
  try {
    if (!old && rendered) {
      mount(rendered, parent, inst._placeholder, svg);
      inst._placeholder?.parentNode?.removeChild(inst._placeholder);
      inst._placeholder = null;
    } else if (old && !rendered) {
      unmount(old);
      inst._placeholder = document.createComment("");
      parent.insertBefore(inst._placeholder, anchor);
    } else if (old && rendered) {
      patch(parent, old, rendered, svg);
    }
    inst._vnode = rendered;
  } catch (error) {
    // Both trees can own work when a child throws partway through a patch.
    const errors: unknown[] = [error];
    unmount(rendered, true, errors);
    unmount(old, true, errors);
    inst._vnode = null;
    inst._placeholder ??= document.createComment("");
    parent.insertBefore(inst._placeholder, anchor);
    raise(errors);
  }
}

function assignBoundary(vnode: VNode, inst: Instance) {
  const rendered = inst._vnode;
  if (rendered && isSpec(rendered.type) && rendered._instance) {
    assignBoundary(rendered, rendered._instance);
  }
  vnode._dom = rendered?._dom ?? inst._placeholder;
  vnode._end = rendered?._end ?? null;
}

function patchComponent(oldV: VNode, newV: VNode, svg = false) {
  const inst = oldV._instance!;
  newV._instance = inst;
  const nextProps = newV.props ?? {};
  const skip = !inst._dirty && shallowEqual(inst.props, nextProps);
  inst.props = nextProps;
  inst._svg = svg;
  if (skip) {
    assignBoundary(newV, inst);
    return;
  }
  inst._dirty = false;
  const rendered = renderInstance(inst);
  patchRendered(inst, rendered, svg);
  assignBoundary(newV, inst);
  if (inst._mounted) callHook(inst, "onUpdate", "_didUpdate");
}

function updateInstance(inst: Instance) {
  if (inst._destroyed || !inst._parent || !inst._dirty) return;
  inst._dirty = false;
  const rendered = renderInstance(inst);
  patchRendered(inst, rendered, inst._svg);
  if (inst._mounted) callHook(inst, "onUpdate", "_didUpdate");
}

function patchChildren(parent: Node, oldCh: VNode[], newCh: VNode[], svg = false, boundary: Node | null = null) {
  if (import.meta.env.DEV) warnKeys(newCh);

  const oldLen = oldCh.length;
  const newLen = newCh.length;
  if (oldLen === newLen && oldLen) {
    let i = 0;
    for (; i < oldLen; i++) if (oldCh[i].key == null || oldCh[i].key !== newCh[i].key) break;
    if (i === oldLen) {
      for (let j = 0; j < newLen; j++) patch(parent, oldCh[j], newCh[j], svg);
      return;
    }
  }

  const oldByKey = new Map<string | number, VNode>();
  const oldUnkeyed = new Map<VNode["type"], VNode[]>();
  for (const o of oldCh) {
    if (o.key != null) oldByKey.set(o.key, o);
    else {
      const bucket = oldUnkeyed.get(o.type);
      if (bucket) bucket.push(o);
      else oldUnkeyed.set(o.type, [o]);
    }
  }

  const used = new Set<VNode>();
  const unkeyedAt = new Map<VNode["type"], number>();

  for (const n of newCh) {
    let match: VNode | undefined;
    if (n.key != null) {
      const keyed = oldByKey.get(n.key);
      if (keyed && keyed.type === n.type) match = keyed;
    } else {
      const bucket = oldUnkeyed.get(n.type);
      const at = unkeyedAt.get(n.type) ?? 0;
      const candidate = bucket?.[at];
      if (candidate) {
        match = candidate;
        unkeyedAt.set(n.type, at + 1);
      }
    }
    if (match) {
      used.add(match);
      patch(parent, match, n, svg);
    } else {
      mount(n, parent, boundary, svg);
    }
  }

  for (const o of oldCh) {
    if (!used.has(o)) unmount(o);
  }

  let next = boundary;
  for (let i = newLen - 1; i >= 0; i--) {
    const vnode = newCh[i];
    moveVNode(parent, vnode, next);
    const start = liveStart(vnode);
    if (start) next = start;
  }
}

const ROOTS = new WeakMap<Element, VNode>();

function asRootVNode(spec: ComponentSpec | VNode): VNode {
  if (isSpec(spec)) return { type: spec, props: {}, children: [], key: undefined };
  return spec;
}

function propsFromDom(el: Element): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const attr of el.attributes) props[attr.name] = attr.value;
  return props;
}

function hydrateVNode(vnode: VNode, parent: Node, node: Node | null, svg = false): Node | null {
  if (vnode.type === TEXT) {
    if (node?.nodeType !== 3) {
      mount(vnode, parent, node, svg);
      return node;
    }
    vnode._dom = node;
    const value = String(vnode.props.nodeValue ?? "");
    if (node.nodeValue !== value) node.nodeValue = value;
    return node.nextSibling;
  }

  if (vnode.type === FRAGMENT) {
    const start = document.createComment("");
    parent.insertBefore(start, node);
    vnode._dom = start;
    let cursor = node;
    for (const child of vnode.children) cursor = hydrateVNode(child, parent, cursor, svg);
    const end = document.createComment("");
    parent.insertBefore(end, cursor);
    vnode._end = end;
    return cursor;
  }

  if (isSpec(vnode.type)) {
    const inst = makeInstance(vnode.type, vnode.props);
    vnode._instance = inst;
    inst._parent = parent;
    inst._svg = svg;
    callHook(inst, "onBeforeMount", "_beforeMount");
    const rendered = renderInstance(inst);
    inst._vnode = rendered;
    let cursor = node;
    if (rendered) {
      cursor = hydrateVNode(rendered, parent, node, svg);
    } else {
      inst._placeholder = document.createComment("");
      parent.insertBefore(inst._placeholder, node);
    }
    inst._mounted = true;
    vnode._dom = rendered?._dom ?? inst._placeholder;
    vnode._end = rendered?._end ?? null;
    queueMount(inst);
    return cursor;
  }

  const tag = vnode.type as string;
  const elementSvg = svg || tag === "svg";
  if (
    node?.nodeType !== 1 ||
    (node as Element).localName !== tag ||
    ((node as Element).namespaceURI === SVG_NS) !== elementSvg
  ) {
    mount(vnode, parent, node, svg);
    return node;
  }

  const el = node as Element;
  vnode._dom = el;
  const childSvg = elementSvg && tag !== "foreignObject";
  patchProps(el, propsFromDom(el), vnode.props);
  if (!vnode.props.dangerouslySetInnerHTML) {
    let cursor: Node | null = el.firstChild;
    for (const child of vnode.children) cursor = hydrateVNode(child, el, cursor, childSvg);
    while (cursor) {
      const next = cursor.nextSibling;
      el.removeChild(cursor);
      cursor = next;
    }
  } else if (import.meta.env.DEV && vnode.children.length) {
    console.warn("SvenJS: dangerouslySetInnerHTML ignored children");
  }
  applyFormProps(el, tag, vnode.props);
  applyRef(el, vnode.props.ref);
  return el.nextSibling;
}

function commitRoot(container: Element, next: VNode, prev: VNode | undefined, write: () => void) {
  const errors: unknown[] = [];
  beginCommit();
  try {
    write();
    ROOTS.set(container, next);
  } catch (error) {
    errors.push(error);
    errors.push(...commits[commits.length - 1].errors.splice(0));
    ROOTS.delete(container);
    unmount(next, true, errors);
    unmount(prev, true, errors);
    container.replaceChildren();
  }
  // Successful nested roots keep their mount hooks; failed instances are skipped.
  runUser(endCommit, errors);
  raise(errors);
}

export function render(spec: ComponentSpec | VNode, container: Element | null): string | void {
  if (!container) return "Error: No node to attach";
  const next = adopt(asRootVNode(spec))!;
  const prev = ROOTS.get(container);
  commitRoot(container, next, prev, () => {
    if (prev) patch(container, prev, next);
    else mount(next, container, null);
  });
}

export function hydrate(spec: ComponentSpec | VNode, container: Element | null): string | void {
  if (!container) return "Error: No node to attach";
  if (ROOTS.has(container)) return render(spec, container);
  const next = adopt(asRootVNode(spec))!;
  commitRoot(container, next, undefined, () => {
    let cursor = hydrateVNode(next, container, container.firstChild);
    while (cursor) {
      const following = cursor.nextSibling;
      container.removeChild(cursor);
      cursor = following;
    }
  });
}

export function unmountRoot(container: Element) {
  const prev = ROOTS.get(container);
  if (!prev) return;
  ROOTS.delete(container);
  unmount(prev);
}

function applyFormProps(el: Element, tag: string, props: Record<string, any>) {
  if ((tag === "textarea" || tag === "input") && "value" in props) {
    const next = props.value == null ? "" : String(props.value);
    if ((el as HTMLTextAreaElement).value !== next) (el as HTMLTextAreaElement).value = next;
  } else if (tag === "select" && props.value != null) {
    (el as HTMLSelectElement).value = String(props.value);
  }
  if (tag === "input" && "checked" in props) (el as HTMLInputElement).checked = Boolean(props.checked);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => (ch === "&" ? "&amp;" : ch === "<" ? "&lt;" : ch === ">" ? "&gt;" : "&quot;"));
}

function attrsToString(props: Record<string, any>, tag?: string): string {
  let out = "";
  for (const name in props) {
    if (!validAttributeName(name)) continue;
    if (name === "key" || name === "children" || name === "ref" || name === "dangerouslySetInnerHTML") continue;
    if (eventName(name)) continue;
    if ((tag === "textarea" || tag === "select") && name === "value") continue;
    if (name === "className" || name === "class") {
      if (name === "class" && props.className) continue;
      const c = props.className ?? props.class;
      if (c) out += ` class="${escapeHtml(String(c))}"`;
      continue;
    }
    const val = props[name];
    if (name.startsWith("aria-") || name.startsWith("data-")) {
      if (val == null) continue;
      const text = val === true ? "true" : val === false ? "false" : String(val);
      out += ` ${name}="${escapeHtml(text)}"`;
      continue;
    }
    if (val == null || val === false || typeof val === "function") continue;
    if (name === "style") {
      const style = styleText(val);
      if (style) out += ` style="${escapeHtml(style)}"`;
      continue;
    }
    if (name === "htmlFor") {
      out += ` for="${escapeHtml(String(val))}"`;
      continue;
    }
    if (val === true) {
      out += ` ${name}`;
      continue;
    }
    out += ` ${name}="${escapeHtml(String(val))}"`;
  }
  return out;
}

function stringify(vnode: VNode | null, selectedValue?: unknown, text?: string[]): string {
  if (!vnode) return "";
  if (vnode.type === TEXT) {
    const value = String(vnode.props.nodeValue ?? "");
    text?.push(value);
    return escapeHtml(value);
  }
  if (vnode.type === FRAGMENT) {
    return vnode.children.map((c) => stringify(c, selectedValue, text)).join("");
  }
  if (isSpec(vnode.type)) {
    const inst = makeInstance(vnode.type, vnode.props);
    callHook(inst, "onBeforeMount", "_beforeMount");
    return stringify(renderInstance(inst), selectedValue, text);
  }
  const tag = vnode.type as string;
  if (!validAttributeName(tag)) throw new TypeError("SvenJS: bad tag");
  const inner = vnode.props.dangerouslySetInnerHTML?.__html;
  const optionText: string[] | undefined = tag === "option" ? [] : text;
  let body = "";
  if (!VOID.has(tag)) {
    if (inner != null) body = String(inner);
    else if (tag === "textarea" && "value" in vnode.props) body = escapeHtml(String(vnode.props.value ?? ""));
    else for (const c of vnode.children) body += stringify(c, tag === "select" ? vnode.props.value : selectedValue, optionText);
  }
  let extra = "";
  if (tag === "option" && selectedValue != null) {
    const value = vnode.props.value != null ? String(vnode.props.value) : optionText!.join("").replace(/[\t\n\f\r ]+/g, " ").trim();
    if (value === String(selectedValue)) extra = " selected";
  }
  const open = `<${tag}${attrsToString(vnode.props, tag)}${extra}>`;
  return VOID.has(tag) ? open : `${open}${body}</${tag}>`;
}

export function renderToString(spec: ComponentSpec | VNode): string {
  return stringify(asRootVNode(spec));
}
